// ChatWindow.js
import React, { useState, useCallback, useEffect, useRef } from "react";

import { useChatRefs }         from "../hooks/useChatRefs";
import { useMessageSelection } from "../hooks/useMessageSelection";
import { useChatScroll }       from "../hooks/useChatScroll";
import { useTyping }           from "../hooks/useTyping";
import { useSwipeReply }       from "../hooks/useSwipeReply";
import { useSendMessage }      from "../hooks/useSendMessage";
import { useScheduledMessage } from "../hooks/useScheduledMessage";
import { useVoiceRecorder }    from "./recording";
import { useMessageAction }    from "../hooks/useMessageAction";
import { useScheduleAction }   from "../hooks/useScheduleAction";

import UserInfoOverlay    from "./UserInfoOverlay";
import GroupInfoOverlay   from "./GroupInfoOverlay";
import SelectionActionBar from "./SelectionActionBar";
import ChatHeader         from "./ChatHeader";
import MessageList        from "./MessageList";
import ChatInput          from "./ChatInput";
import EmojiPicker, { insertEmojiAtCursor } from "./emoji";
import { saveMessage } from "../db/fsDB";
import ForwardModal    from "./ForwardModal";
import ScheduleModal   from "./ScheduleModal";
import MediaPreviewBar from "./MediaPreviewbar";
import MediaLightbox   from "./MediaLightbox";

import { useChatWindowEffects } from "../hooks/useChatWindowEffects";

import { uploadToR2 }  from "./Upload";
import { uploadMedia } from "../utils/MediaUtils";
import {
  uuid,
  renderSystemText,
  renderTextWithLinks,
} from "../utils/ChatUtils";
import { toastSuccess, toastInfo } from "../utils/toast";

// ─── Centralised msgId resolver ────────────────────────────────────────────
function resolveMsgId(msg) {
  return (
    msg?.id ||
    msg?.messageId ||
    msg?.raw?.key?.msgId
  );
}

// ─── Centralised senderChatId resolver ─────────────────────────────────────
function resolveSenderChatId(msg) {
  return (
    msg?.senderChatId ||
    msg?.raw?.key?.senderChatId
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   📌 PIN DURATION PICKER MODAL
─────────────────────────────────────────────────────────────────────────────── */
function PinDurationModal({ onConfirm, onClose }) {
  const options = [
    { key: "24h", label: "24 hours" },
    { key: "7d",  label: "7 days" },
    { key: "30d", label: "30 days" },
    { key: "60d", label: "60 days" },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xs rounded-2xl bg-white dark:bg-neutral-900 overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-5 pb-3">
          <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            Pin message for…
          </p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Everyone in this chat will see the pinned message.
          </p>
        </div>
        <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
        <div className="py-1">
          {options.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => onConfirm(key)}
              className="w-full px-5 py-3.5 text-left text-sm text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 transition-colors touch-manipulation"
            >
              {label}
            </button>
          ))}
          <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
          <button
            onClick={onClose}
            className="w-full px-5 py-3.5 text-left text-sm text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 transition-colors touch-manipulation"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   📌 PINNED MESSAGE BANNER
─────────────────────────────────────────────────────────────────────────────── */
function PinnedBanner({ pins, messages, chatKey, onScrollTo, onUnpin, myChatId, groupMembers, selectedItem }) {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (activeIndex >= pins.length) setActiveIndex(0);
  }, [pins.length]);

  if (!pins.length) return null;

  const orderedPins = [...pins].reverse();
  const current = orderedPins[activeIndex];

  const msg = (messages[chatKey] || []).find(
    (m) => (m.id || m.messageId || m?.raw?.key?.msgId) === current.msgId
  );
  const previewText =
    msg?.text ||
    msg?.raw?.message?.conversation ||
    (msg?.media ? "📎 Media" : "Pinned message");

  const handleClick = () => {
    onScrollTo(current.msgId);
    setActiveIndex((i) => (i + 1) % orderedPins.length);
  };

  let canUnpin = true;
  if (selectedItem?.type === "group") {
    const members = groupMembers?.[selectedItem.groupId] || [];
    const me = members.find((m) => String(m.chatId) === String(myChatId));
    canUnpin = me?.isAdmin || me?.isSuperAdmin;
  }

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 shadow-sm">
      <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="currentColor"
        className="shrink-0 text-green-600 dark:text-green-400"
      >
        <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
      </svg>

      <button onClick={handleClick} className="flex-1 min-w-0 text-left">
        <div className="text-[11px] font-semibold text-green-600 dark:text-green-400 leading-tight">
          {orderedPins.length > 1
            ? `Pinned message ${activeIndex + 1} of ${orderedPins.length}`
            : "Pinned message"}
        </div>
        <div className="text-xs text-neutral-600 dark:text-neutral-300 truncate leading-tight mt-0.5">
          {previewText}
        </div>
      </button>

      {canUnpin && (
        <button
          onClick={(e) => { e.stopPropagation(); onUnpin(current.msgId); }}
          className="shrink-0 p-1 rounded-full text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation"
          title="Unpin message"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      )}
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="12"
      height="12"
      fill="currentColor"
      className="shrink-0"
    >
      <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm-3 8V6a3 3 0 0 1 6 0v3z" />
    </svg>
  );
}
/* ─────────────────────────────────────────────────────────────────────────────
   🔒 ENCRYPTION NOTICE
─────────────────────────────────────────────────────────────────────────────── */

function EncryptionNotice() {
  return (
    <div className="flex justify-center px-4 py-1 select-none">
      <div
        className="
          max-w-[88%] sm:max-w-[75%]
          rounded-lg
          bg-[#FEF6DC] dark:bg-neutral-800/80
          px-3 py-2
          text-center
          shadow-sm
        "
      >
        <p className="flex items-center justify-center gap-1.5 text-[12px] leading-snug text-[#54656f] dark:text-neutral-300">
          <LockIcon />
          <span>
            Messages are end-to-end encrypted. No one outside of this chat, not even Metisel, can read or listen to them.
          </span>
        </p>
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
   MAIN ChatWindow
─────────────────────────────────────────────────────────────────────────────── */
export default function ChatWindow(props) {
  let {
    users, selectedItem, setSelectedItem, isActive = true,
    messages, setMessages,
    typingUsers, groupTyping, groupMembers, setGroupMembers,
    groups, setUsers, currentUser, socket,
    input, setInput, selectedReply, setSelectedReply,
      setActiveTab, activeTab, TABS, mobileView, typingTimeoutRef,
    getUserKey, getGroupKey,
    pendingMedia, setPendingMedia, setIsUploading, setUploadProgress,
    showToast: showToastProp,
    resolveSenderChatId: resolveSenderChatIdProp,
    p2pOrders,
      chatMessages,
  chatKey,
  fallbackItem,
    orderTimers,
    activeP2POrder,
    setGroups,
    feedSocket,
    cryptoFns,
    scheduledMessages,
       onConfirmPayment,
  } = props;
  const showToast = useCallback(
    (msg, type = "info") => {
      if (typeof showToastProp === "function") return showToastProp(msg, type);
      if (type === "error")   return toastInfo(msg);
      if (type === "success") return toastSuccess(msg);
      toastInfo(msg);
    },
    [showToastProp]
  );

const [showUserInfo,     setShowUserInfo]     = useState(false);
const [userInfoInitialTab, setUserInfoInitialTab] = useState("posts");
  const [showGroupMembers, setShowGroupMembers] = useState(false);
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [selectionMode,    setSelectionMode]    = useState(false);
  const [showEmoji,        setShowEmoji]        = useState(false);
  const [recentEmojis,     setRecentEmojis]     = useState([]);
  const [activeMedia,      setActiveMedia]      = useState(null);
  const [mutualGroups,     setMutualGroups]     = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [groupInviteLink,  setGroupInviteLink]  = useState(null);
  const [forwardMessages,  setForwardMessages]  = useState(null);
  const [userFollowState,  setUserFollowState]  = useState({
    isFollowing: false, followersCount: 0, followingCount: 0,
  });

  const [deleteTarget,    setDeleteTarget]    = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting,      setIsDeleting]      = useState(false);

  const [pinTarget,    setPinTarget]    = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);

  // ── Inline edit state (regular messages) ──────────────────────────────────
  const [editTarget, setEditTarget] = useState(null);

  // ── Inline edit state (scheduled messages) ────────────────────────────────
  // Shape: { msgId, currentText, scheduledAt, batchId? }
  const [scheduleEditTarget, setScheduleEditTarget] = useState(null);

  // ── Schedule modal state ───────────────────────────────────────────────────
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // ── Scroll-to-bottom button state ──────────────────────────────────────────
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  const replyRef = React.useRef(null);
  const REPLY_PREVIEW_HEIGHT = 56;

  const myChatId = currentUser?.chatInfo?.[0]?.chatId?.toString();

   const myName =
        currentUser?.chatInfo?.[0]?.userName  ||
        currentUser?.chatInfo?.[0]?.username  ||
        currentUser?.username                 ||
        currentUser?.name                     ||
        myChatId                              ||
        "Me";

  const isRemovedFromGroup =
    selectedItem?.type === "group" &&
    selectedItem?.groupId &&
    Array.isArray(groupMembers?.[selectedItem.groupId]) &&
    !groupMembers[selectedItem.groupId].some(
      (m) => String(m.chatId) === String(myChatId)
    );

  const liveGroup =
    selectedItem?.type === "group"
      ? groups.find((g) => g.groupId === selectedItem.groupId)
      : null;

  // 🟢 Live presence for the header dot — selectedItem is a frozen
  // snapshot from when the row was clicked, so re-derive from `users`.
  const liveSelectedUser =
    selectedItem?.type === "user"
      ? users.find((u) => String(u.chatId) === String(selectedItem.chatId))
      : null;

          if (selectedItem?.type === "user") {
  // Never trust the passed-in activeP2POrder's cached status blindly —
  // re-check it against the live p2pOrders array by orderId. Without
  // this, a completed/expired order that hasn't been cleared upstream
  // yet (e.g. a timing gap before GroupChat's own clearing logic runs)
  // would keep rendering its order badge in ChatHeader indefinitely.
  if (activeP2POrder?.orderId) {
    const latest = p2pOrders?.find(
      (o) => String(o.orderId) === String(activeP2POrder.orderId)
    );

    activeP2POrder =
      latest && ["pending", "paid"].includes(latest.status)
        ? latest
        : null;
  }

  if (!activeP2POrder) {
    activeP2POrder =
      p2pOrders?.find(
        (o) =>
          (
            String(o.sellerChatId) === String(selectedItem.chatId) ||
            String(o.buyerChatId) === String(selectedItem.chatId)
          ) &&
       ["pending", "paid"].includes(o.status)
      ) || null;
  }
}
 
const remainingTime =
  activeP2POrder
    ? orderTimers?.[
        activeP2POrder.orderId
      ] ?? null
    : null;
  const { fileInputRef, longPressTimer, longPressTriggered } = useChatRefs();
  const inputRef      = React.useRef(null);
 const chatWindowRef = React.useRef(null);
  // Owned per ChatWindow instance — not passed in as a prop. Both
  // MobileChatPanel and DesktopChatLayout now keep every opened chat
  // mounted simultaneously; a ref passed down from a shared parent
  // would be overwritten by whichever instance mounts/commits last,
  // so each instance must hold its own scrollable-container ref.
  const chatBodyRef   = React.useRef(null);

  useEffect(() => {
    const container = chatBodyRef?.current;
    if (!container) return;
    const onScroll = () => {
      const dist = container.scrollHeight - container.scrollTop - container.clientHeight;
      setShowScrollBtn(dist > 200);
    };
    container.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => container.removeEventListener("scroll", onScroll);
  }, [chatBodyRef, selectedItem]);

  useEffect(() => { setShowScrollBtn(false); }, [selectedItem]);

// Clear both edit targets when the selected chat changes.
// Key on the chat's identity, not the selectedItem object reference —
// selectedItem is recreated on unrelated re-renders (presence/group
// syncs etc.), and since the desktop layout keeps ChatWindow mounted
// continuously, those reference changes fired constantly and reset
// `input` to "" right after each keystroke.
const selectedChatIdentity =
  selectedItem?.type === "group" ? selectedItem.groupId : selectedItem?.chatId;

useEffect(() => {
  setEditTarget(null);
  setScheduleEditTarget(null);
  setInput("");
}, [selectedChatIdentity]);
   const { unreadDividerRef } = useChatWindowEffects({
    myChatId, socket, cryptoFns, selectedItem, setSelectedItem,
    setMessages, getUserKey, getGroupKey, setGroupMembers, setUsers,
    setGroups, liveGroup, showGroupMembers, setGroupInviteLink, setRecentEmojis,
    selectedReply, inputRef, chatBodyRef, messages, showToast,
    users, groups,
    // Lets the hook tell "chat selected" apart from "chat actually
    // on screen" — mobile keeps ChatWindow mounted behind other tabs.
    activeTab, TABS, mobileView,
     isActive,
  });

  useEffect(() => {
    if (!socket) return;
    if (selectedItem?.type === "group") {
      socket.emit("group:join", { groupId: selectedItem.groupId });
    }
    return () => {
      if (selectedItem?.type === "group") {
        socket.emit("group:leave", { groupId: selectedItem.groupId });
      }
    };
  }, [selectedItem, socket]);

  React.useEffect(() => {
    if (!showUserInfo || !selectedItem || selectedItem.type !== "user") return;
    const liveUser = users.find((u) => String(u.chatId) === String(selectedItem.chatId));
    if (!liveUser) return;
    setUserFollowState({
      isFollowing:    liveUser.isFollowing    ?? false,
      followersCount: liveUser.followersCount ?? 0,
      followingCount: liveUser.followingCount ?? 0,
    });
  }, [users, showUserInfo, selectedItem]);

  const {
    toggleMessageSelect,
    clearSelection,
    handleMouseDown,
    handleMouseUp,
    handleMouseLeave,
  } = useMessageSelection({
    selectedMessages, setSelectedMessages,
    selectionMode, setSelectionMode,
    longPressTimer, longPressTriggered,
  });

  const { deleteMessages, clearChat, editMessage, pinMessage, unpinMessage } = useMessageAction({
    socket,
    myChatId,
    selectedItem,
    setUsers,
    setGroups,
    setMessages,
    getUserKey,
    getGroupKey,
    groupMembers,
  });

  // ── Schedule-specific actions (delete + edit scheduled messages) ───────────
  // Pass allMessages so batch operations can find sibling messages in other chats
  const { deleteSchedule, editSchedule } = useScheduleAction({
    socket,
    myChatId,
    selectedItem,
    setMessages,
    getUserKey,
    getGroupKey,
    allMessages: messages,   // ← NEW: enables cross-chat batch delete/edit
  });

  const handleFollow   = () => feedSocket?.followUser?.({ targetChatId: selectedItem.chatId });
  const handleUnfollow = () => feedSocket?.unfollowUser?.({ targetChatId: selectedItem.chatId });
const loadUserInfo = (targetChatId, initialTab = "posts") => {
  const targetUser = users?.find((u) => String(u.chatId) === String(targetChatId));
    setUserFollowState({
      isFollowing:    targetUser?.isFollowing ?? false,
      followersCount: null,
      followingCount: null,
    });
    feedSocket.getFollowCounts({ targetChatId });
    const mutual = (groups || []).filter((g) => {
      const mem = groupMembers?.[g.groupId] || g.members || [];
      return (
        Array.isArray(mem) && mem.length > 0 &&
        mem.some((m) => String(m.chatId) === String(myChatId)) &&
        mem.some((m) => String(m.chatId) === String(targetChatId))
      );
    });
      setMutualGroups(mutual);
    setUserInfoInitialTab(initialTab);
    setShowUserInfo(true);
  
  };

   const clearPendingMedia = useCallback(() => {
    const items = Array.isArray(pendingMedia) ? pendingMedia : (pendingMedia ? [pendingMedia] : []);
    items.forEach((item) => {
      if (item?.previewUrl) {
        try { URL.revokeObjectURL(item.previewUrl); } catch (_e) {}
      }
    });
    setPendingMedia(null);
    setUploadProgress(0);
  }, [pendingMedia, setPendingMedia, setUploadProgress]);
  const sendMessageRef = React.useRef(null);
  const voiceMediaRef  = React.useRef(null);

  const {
    isRecording,
    isPaused,
    recordingSeconds,
    previewUrl: voicePreviewUrl,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    cancelRecording,
  } = useVoiceRecorder({
    onComplete: useCallback(async (file, blobUrl) => {
      if (!file) return;

      const voiceMedia = {
        file,
        previewUrl: blobUrl,
        type:       "audio",
        mimeType:   file.type || "audio/webm",
        fileName:   file.name,
        fileSize:   file.size,
        isVoice:    true,
      };

      voiceMediaRef.current = voiceMedia;
      setPendingMedia(voiceMedia);

      await new Promise((r) => setTimeout(r, 0));

      if (typeof sendMessageRef.current === "function") {
        await sendMessageRef.current(file);
      }

      voiceMediaRef.current = null;
    }, [setPendingMedia]),
  });
  const activeChatKey =
    chatKey ??
    (selectedItem
      ? selectedItem.type === "group"
        ? getGroupKey(selectedItem.groupId)
        : getUserKey(selectedItem.chatId)
      : null);
  const activeChatMessages =
    chatMessages ?? (activeChatKey ? messages[activeChatKey] || [] : []);
  const scrollChatMessages =
    chatMessages ?? (chatKey ? messages[chatKey] || [] : []);

      const { scrollToBottom } = useChatScroll({
    chatBodyRef,
    messages: activeChatKey ? activeChatMessages : [],
    chatKey: activeChatKey,
    groupTyping,
    typingUsers,
    myChatId,
  });

  const { handleInput, renderTypingForSelected } = useTyping({
    socket, selectedItem, currentUser, typingUsers, groupTyping,
    typingTimeoutRef, myChatId, findUsernameByChatId, setInput,
  });

  const {
    onRowTouchStart,
    onRowTouchMove,
    onRowTouchEnd,
    onRowTouchCancel,
  } = useSwipeReply({
    setSelectedReply,
    uuid,
    selectionMode,
    onLongPress: (msgId) => {
      setSelectedMessages((prev) =>
        prev.includes(msgId) ? prev : [...prev, msgId]
      );
      setSelectionMode(true);
    },
    onTap: (msgId) => {
      setSelectedMessages((prev) => {
        if (prev.includes(msgId)) {
          const next = prev.filter((id) => id !== msgId);
          if (next.length === 0) setSelectionMode(false);
          return next;
        }
        return [...prev, msgId];
      });
    },
  });

  /* ══════════════════════════════════════════════════════════════════════════
     useSendMessage — normal send
  ══════════════════════════════════════════════════════════════════════════ */
  const { sendMessage, isSending } = useSendMessage({
    socket,
    myChatId,
    selectedItem,
    setMessages,
    setUsers,
    setGroups,
    input,
    setInput,
    selectedReply,
    setSelectedReply,
    pendingMedia,
    voiceMediaRef,
    setPendingMedia,
    uploadMedia,
    clearPendingMedia,
    setIsUploading,
    setUploadProgress,
    groupMembers,
    getUserKey,
    getGroupKey,
    cryptoFns,
    showToast,
    scrollToBottom,
    uuid,
  });

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  /* ══════════════════════════════════════════════════════════════════════════
     useScheduledMessage — schedule:create / schedule:* events
  ══════════════════════════════════════════════════════════════════════════ */
  const {
    scheduleMessage,
    scheduleMessageForItem,
    isSending: isScheduling,
    scheduled,
    updateScheduled,
    cancelScheduled,
    formatTime: formatScheduledTime,
  } = useScheduledMessage({
    socket,
    myChatId,
    selectedItem,
    setMessages,
    setUsers,
    setGroups,
    users,
    groups,
    input,
    setInput,
    selectedReply,
    setSelectedReply,
    pendingMedia,
    setPendingMedia,
    voiceMediaRef,
    uploadMedia,
    clearPendingMedia,
    setIsUploading,
    setUploadProgress,
    groupMembers,
    getUserKey,
    getGroupKey,
    cryptoFns,
    showToast,
    scrollToBottom,
    uuid,
    initialScheduled: scheduledMessages ?? [],
  });

  /* ══════════════════════════════════════════════════════════════════════════
     Schedule modal flow
  ══════════════════════════════════════════════════════════════════════════ */
    const handleOpenScheduleModal = useCallback(() => {
    const hasMedia = Array.isArray(pendingMedia)
      ? pendingMedia.length > 0
      : Boolean(pendingMedia);

    if (!input.trim() && !hasMedia) {
      showToast("Type a message first to schedule it.", "error");
      return;
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setShowScheduleModal(true);
  }, [input, pendingMedia, showToast]);
  const handleScheduleSelected = useCallback(
    ({ msgId, text, media }) => {
      if (text) setInput(text);
      if (media) {
        setPendingMedia({
          previewUrl: media.url      ?? null,
          mimeType:   media.mimeType ?? null,
          fileName:   media.fileName ?? null,
          fileSize:   media.fileSize ?? null,
          isVoice:    media.isVoice  ?? false,
          type:       media.type     ?? "document",
        });
      }
      setShowScheduleModal(true);
    },
    [setInput, setPendingMedia]
  );

  /* ══════════════════════════════════════════════════════════════════════════
     handleConfirmSchedule — multi-recipient scheduling
     NEW: generate a shared batchId so all messages in this send can be
          linked together for batch delete / edit later.
  ══════════════════════════════════════════════════════════════════════════ */
   const handleConfirmSchedule = useCallback(
    async (targets, chosenDate) => {
      setShowScheduleModal(false);

      const textSnapshot  = input;
      const mediaSnapshot = pendingMedia || voiceMediaRef?.current || null;
      const mediaItems    = Array.isArray(mediaSnapshot)
        ? mediaSnapshot
        : mediaSnapshot
        ? [mediaSnapshot]
        : [];
      const replySnapshot = selectedReply;

      const timeStr = chosenDate.toLocaleTimeString([], {
        hour:   "2-digit",
        minute: "2-digit",
      });

      // One batchId ties together all recipients AND all media items
      // of this scheduled send.
      const batchId = (targets.length > 1 || mediaItems.length > 1) ? uuid() : null;

      for (let i = 0; i < targets.length; i++) {
        const target = targets[i];

        const targetItem =
          target.type === "group"
            ? { type: "group", groupId: target.groupId, name: target.label }
            : { type: "user",  chatId:  target.chatId,  name: target.label };

        const clearAfter = i === 0;

               if (mediaItems.length > 0) {
          for (let j = 0; j < mediaItems.length; j++) {
            await scheduleMessageForItem({
              textToSend:    j === 0 ? textSnapshot : "",
              mediaFile:     mediaItems[j]?.file ?? null,
              scheduledAt:   chosenDate,
              targetItem,
              replySnapshot: j === 0 ? replySnapshot : null,
              mediaSnapshot: mediaItems[j],
              clearAfter:    clearAfter && j === 0,
              silent:        true,
              batchId,
            });
          }
        } else {
          await scheduleMessageForItem({
            textToSend:    textSnapshot,
            mediaFile:     null,
            scheduledAt:   chosenDate,
            targetItem,
            replySnapshot,
            mediaSnapshot: null,
            clearAfter,
            silent:        true,
            batchId,
          });
        }
      }

      const recipientLabel =
        targets.length === 1
          ? targets[0].label
          : `${targets.length} recipients`;

      showToast(`⏳ Scheduled for ${timeStr} → ${recipientLabel}`, "success");
    },
    [
      scheduleMessageForItem,
      input,
      pendingMedia,
      voiceMediaRef,
      selectedReply,
      showToast,
      uuid,
    ]
  );
  /* ══════════════════════════════════════════════════════════════════════════
     Inline edit handlers — REGULAR messages
  ══════════════════════════════════════════════════════════════════════════ */
  const handleStartEdit = useCallback(({ msgId, currentText }) => {
    setScheduleEditTarget(null);
    setEditTarget({ msgId, currentText });
    setInput(currentText);
    setTimeout(() => {
      const el = document.querySelector(".chat-text-input");
      if (!el) return;
      el.focus();
      const len = el.value?.length || 0;
      el.setSelectionRange(len, len);
    }, 50);
  }, [setInput]);

  const handleCancelEdit = useCallback(() => {
    setEditTarget(null);
    setInput("");
  }, [setInput]);

  const handleConfirmEdit = useCallback(() => {
    if (!editTarget || !input.trim()) return;
    editMessage({ msgId: editTarget.msgId, newText: input.trim() });
    setEditTarget(null);
    setInput("");
    showToast("Message edited", "success");
  }, [editTarget, input, editMessage, setInput, showToast]);

  /* ══════════════════════════════════════════════════════════════════════════
     Inline edit handlers — SCHEDULED messages
     Uses editSchedule (useScheduleAction) instead of editMessage.
     Passes batchId so all sibling messages are updated together.
  ══════════════════════════════════════════════════════════════════════════ */
  const handleStartScheduleEdit = useCallback(({ msgId, currentText, scheduledAt, batchId }) => {
    setEditTarget(null);
    setScheduleEditTarget({ msgId, currentText, scheduledAt, batchId });
    setInput(currentText);
    setTimeout(() => {
      const el = document.querySelector(".chat-text-input");
      if (!el) return;
      el.focus();
      const len = el.value?.length || 0;
      el.setSelectionRange(len, len);
    }, 50);
  }, [setInput]);

  const handleCancelScheduleEdit = useCallback(() => {
    setScheduleEditTarget(null);
    setInput("");
  }, [setInput]);

  const handleConfirmScheduleEdit = useCallback(() => {
    if (!scheduleEditTarget || !input.trim()) return;

    editSchedule({
      msgId:       scheduleEditTarget.msgId,
      newText:     input.trim(),
      scheduledAt: scheduleEditTarget.scheduledAt,
      batchId:     scheduleEditTarget.batchId ?? null,   // ← NEW: propagate batchId
    });

    setScheduleEditTarget(null);
    setInput("");
    showToast("Scheduled message updated", "success");
  }, [scheduleEditTarget, input, editSchedule, setInput, showToast]);

  /* ══════════════════════════════════════════════════════════════════════════
     Other handlers (unchanged)
  ══════════════════════════════════════════════════════════════════════════ */
    const handleExitGroup = useCallback(() => {
    if (!selectedItem || !socket) return;
    socket.emit("group-exit", { groupId: selectedItem.groupId, chatId: myChatId });
    showToast("You left the group");
    setSelectedItem(null);
  }, [selectedItem, socket, myChatId, showToast, setSelectedItem]);

  const onOpenGroup = useCallback((g) => {
    setSelectedItem({
      type: "group",
      groupId: g.groupId,
      name: g.name,
      groupImageUrl: g.groupImageUrl,
    });
  }, [setSelectedItem]);

  const onOpenDirectMessage = useCallback(({ chatId, username }) => {
    setSelectedItem({
      type: "user",
      chatId,
      username: username || "@User",
    });
  }, [setSelectedItem]);

  const handleEmojiSelect = (emoji) => {
    const inputEl = document.querySelector(".chat-text-input");
    if (!inputEl) return;
    insertEmojiAtCursor(inputEl, emoji);
    inputEl.dispatchEvent(new Event("input", { bubbles: true }));
    setInput(inputEl.value);
    setRecentEmojis((prev) => {
      const next = [emoji, ...prev.filter((e) => e !== emoji)].slice(0, 12);
      localStorage.setItem("recent-emojis", JSON.stringify(next));
      return next;
    });
  };

  function findUsernameByChatId(chatId) {
    if (!chatId) return "";
    if (chatId.toString() === myChatId) return "You";
    if (selectedItem?.type === "group") {
      const members = groupMembers?.[selectedItem.groupId] || [];
      const found = members.find((m) => String(m.chatId) === String(chatId));
      if (found) return found.displayName || found.userName || found.username || found.name || "";
      const liveMembers = liveGroup?.members || [];
      const foundLive = liveMembers.find((m) => String(m.chatId) === String(chatId));
      if (foundLive) return foundLive.displayName || foundLive.userName || foundLive.username || foundLive.name || "";
    }
    const foundUser = users?.find((u) => String(u.chatId) === String(chatId));
    if (foundUser) return foundUser.displayName || foundUser.userName || foundUser.username || foundUser.name || chatId.toString();
    return chatId.toString();
  }
  const handleMsgReply = (msg) => {
    const resolvedMsgId = resolveMsgId(msg);
    setSelectedReply({
      msgId: resolvedMsgId,
      text: msg.text || "",
      media: msg.media || null,
      senderName: msg.senderName || msg.username || null,
    });
  };

  const handleMsgForward = (msg) => setForwardMessages([msg]);

  const handleMsgCopy = async (msg) => {
    const text = msg.text || msg.raw?.message?.conversation || "";
    if (!text) { toastInfo("Nothing to copy"); return; }
    await navigator.clipboard.writeText(text);
    toastSuccess("Copied");
  };

  const handleMsgEdit = (msg) => {
    const msgId = resolveMsgId(msg);
    const currentText = msg.text || msg.raw?.message?.conversation || "";
    handleStartEdit({ msgId, currentText });
  };

  const handleMsgDelete = (msg) => {
    setDeleteTarget(msg);
    setShowDeleteModal(true);
  };

  const handleMsgStar = () => toastInfo("Starred (coming soon)");

  const handleMsgPin = (msg) => {
    setPinTarget(resolveMsgId(msg));
    setShowPinModal(true);
  };

  const confirmPin = (duration) => {
    if (!pinTarget) return;
    pinMessage({ msgId: pinTarget, duration });
    setShowPinModal(false);
    setPinTarget(null);
    showToast("Message pinned");
  };

  const handleMsgUnpin = (msgId) => {
    unpinMessage({ msgId });
    showToast("Message unpinned");
  };
const handleRevealMedia = useCallback((msgId) => {
  const resolvedChatKey =
    currentChatKey ??
    (selectedItem
      ? selectedItem.type === "group"
        ? getGroupKey(selectedItem.groupId)
        : getUserKey(selectedItem.chatId)
      : null);

  if (!resolvedChatKey) return;

  setMessages((prev) => {
    const list = prev[chatKey] || [];

    return {
      ...prev,
      [chatKey]: list.map((m) => {
        const id =
          m.id ||
          m.messageId ||
          m?.raw?.key?.msgId;

        if (id !== msgId) return m;
        if (!m.media || m.media._revealed) return m;

        const updatedMessage = {
          ...m,
          media: {
            ...m.media,
            _revealed: true,
          },
        };

        void saveMessage(updatedMessage);

        return updatedMessage;
      }),
    };
  });
}, [
  selectedItem,
  getGroupKey,
  getUserKey,
  setMessages,
]);

  const scrollToPinnedMessage = useCallback((msgId) => {
    const el = document.getElementById(`msg-${msgId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-2", "ring-green-400");
    setTimeout(() => el.classList.remove("ring-2", "ring-green-400"), 1200);
  }, []);

  const handleScrollToBottom = useCallback(() => {
    chatBodyRef.current?.scrollTo({ top: chatBodyRef.current.scrollHeight, behavior: "smooth" });
  }, [chatBodyRef]);

  const doSingleDelete = ({ forEveryone = false }) => {
    const msgId = resolveMsgId(deleteTarget);
    deleteMessages({ messageIds: [msgId], forEveryone });
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleClearChat = () => {
    clearChat();
    showToast("Chat cleared");
  };

  const deleteTargetSentByMe =
    deleteTarget && String(resolveSenderChatId(deleteTarget)) === String(myChatId);

  const deleteCanForEveryone =
    selectedItem?.type === "group"
      ? (() => {
          const members = groupMembers?.[selectedItem.groupId] || [];
          const me = members.find((m) => String(m.chatId) === String(myChatId));
          return me?.isAdmin || me?.isSuperAdmin || deleteTargetSentByMe;
        })()
      : deleteTargetSentByMe;

/* Use the active chat supplied by GroupChat.
   This keeps ChatWindow aligned with the preloaded
   IDB state and avoids rebuilding the active key. */
const currentChatKey =
  chatKey ??
  (selectedItem
    ? selectedItem.type === "group"
      ? getGroupKey(selectedItem.groupId)
      : getUserKey(selectedItem.chatId)
    : null);
const currentChatMessages =
  chatMessages ??
  (currentChatKey ? messages[currentChatKey] || [] : []);


useEffect(() => {
  // Ignore the initial render...
  if (!currentChatKey) return;

  const last = currentChatMessages[currentChatMessages.length - 1];

}, [currentChatMessages, currentChatKey]);
const currentPins = currentChatMessages

  .filter((m) => {
    if (selectedItem?.type === "group") return m.pinnedBy;
    return m.pinnedBy?.includes(myChatId);
  })
  .map((m) => ({
    msgId: resolveMsgId(m),
    pinnedBy: m.pinnedBy,
    expiresAt: m.expiresAt,
    pinnedAt: m.pinnedAt,
  }));
  const handleBack = () => {
    setSelectedItem(null);
    setActiveTab(TABS.CHATS);
  };

  // ── Derive the single active edit target ChatInput should see ─────────────
  const activeEditTarget    = scheduleEditTarget ?? editTarget;
  const activeOnCancelEdit  = scheduleEditTarget ? handleCancelScheduleEdit : handleCancelEdit;
  const activeOnConfirmEdit = scheduleEditTarget ? handleConfirmScheduleEdit : handleConfirmEdit;
return (
 <div
  ref={chatWindowRef}
  className={`flex flex-col w-full min-w-0 ${
    mobileView &&
    (forwardMessages?.length > 0 || showScheduleModal || (showDeleteModal && deleteTarget) || showPinModal)
      ? "z-[70]"
      : "z-0"
  } bg-transparent ${
    mobileView && !selectedItem
      ? "hidden md:flex"
      : ""
  }`}
  style={
    mobileView
      ? {
          /*
           * ChatInput no longer positions itself against the keyboard.
           * ChatWindow owns the mobile viewport instead.
           *
           * 100dvh follows the dynamic mobile viewport, so when the
           * keyboard opens the available height becomes smaller and
           * the normal-flow composer moves up with it.
           */
          position: "fixed",
          inset: 0,
          height: "100dvh",
          maxHeight: "100dvh",
          overflow: "hidden",
          backgroundImage: "url(/commdoodle.jpg)",
          backgroundRepeat: "repeat",
          backgroundSize: "320px 320px",
          backgroundPosition: "center",
        }
      : {
          height: "100%",
          maxHeight: "100%",
          overflow: "hidden",
          position: "relative",
          width: "100%",
        }
  }
>
     {selectedItem ? (
        <>
          {/* ── Unified header ── */}
          {selectionMode ? (
            <SelectionActionBar
              selectionMode={selectionMode}
              selectedMessages={selectedMessages}
              selectedItem={selectedItem}
              messages={messages}
              setMessages={setMessages}
              getUserKey={getUserKey}
              getGroupKey={getGroupKey}
              socket={socket}
              myChatId={myChatId}
              clearSelection={clearSelection}
              showToast={showToast}
              setSelectedReply={setSelectedReply}
              onForwardMessages={(msgs) => setForwardMessages(msgs)}
              groupMembers={groupMembers}
              pinnedMsgIds={currentPins.map((p) => p.msgId)}
              onStartEdit={handleStartEdit}
              onScheduleSelected={handleScheduleSelected}
              onStartScheduleEdit={handleStartScheduleEdit}
              setUsers={setUsers}
              setGroups={setGroups}
              // ← NEW: pass allMessages so batch ops work in SelectionActionBar too
              allMessages={messages}
            />
          ) : (
          <ChatHeader
             selectedItem={
                selectedItem?.type === "user" && liveSelectedUser
                  ? {
                      ...selectedItem,
                      online: liveSelectedUser.online,
                      lastSeenAt: liveSelectedUser.lastSeenAt ?? null,
                    }
                  : selectedItem
              }
              groupMembers={groupMembers}
              liveGroup={liveGroup}
              renderTypingForSelected={renderTypingForSelected}
              p2pOrder={activeP2POrder}
              remainingTime={remainingTime}
              myChatId={myChatId}
              socket={socket}
              showToast={showToast}
              mobileView={mobileView}
              onBack={handleBack}
              onOpenGroupInfo={() => setShowGroupMembers(true)}
            onOpenUserInfo={() => {
  if (selectedItem?.type === "user") loadUserInfo(selectedItem.chatId, "groups");
}}
              onClearChat={handleClearChat}
              onExitGroup={handleExitGroup}
            />
          )}
{/* 📌 Pinned message banner */}
{!selectionMode && currentPins.length > 0 && (
  <PinnedBanner
    pins={currentPins}
    messages={messages}
    chatKey={currentChatKey}
    onScrollTo={scrollToPinnedMessage}
    onUnpin={handleMsgUnpin}
    myChatId={myChatId}
    groupMembers={groupMembers}
    selectedItem={selectedItem}
  />
)}
{/* Message body */}
<div
  ref={chatBodyRef}
  className="
    relative flex-1 min-h-0
    overflow-y-auto overscroll-contain touch-pan-y
    bg-neutral-50 dark:bg-neutral-900
  "
  style={{
    backgroundImage: "url(/commdoodle.jpg)",
    backgroundRepeat: "repeat",
    backgroundSize: "320px 320px",
    backgroundPosition: "center",
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    touchAction: "pan-y",
  }}
>
       <div
  className="relative flex min-h-full flex-col justify-end px-2 sm:px-3 py-2"
  style={{
    paddingBottom: "8px",
  }}
> 
   
    {!selectionMode && <EncryptionNotice />}

   <MessageList
  messages={messages}
  selectedItem={selectedItem}
  myChatId={myChatId}
  getUserKey={getUserKey}
  getGroupKey={getGroupKey}
  groupMembers={groupMembers}
   unreadDividerRef={unreadDividerRef}
    isActive={isActive}
  users={users}
  myName={myName}
  selectedMessages={selectedMessages}
  selectionMode={selectionMode}
  mobileView={mobileView}
  p2pOrders={p2pOrders}
  setActiveTab={setActiveTab}
  onConfirmPayment={onConfirmPayment}
  TABS={TABS}
  onRowTouchStart={onRowTouchStart}
  onRowTouchMove={onRowTouchMove}
  onRowTouchEnd={onRowTouchEnd}
  onRowTouchCancel={onRowTouchCancel}
  toggleMessageSelect={toggleMessageSelect}
  handleMouseDown={handleMouseDown}
  handleMouseUp={handleMouseUp}
  handleMouseLeave={handleMouseLeave}
  onMessageReply={handleMsgReply}
  onMessageForward={handleMsgForward}
  onMessageCopy={handleMsgCopy}
  onMessageEdit={handleMsgEdit}
  onMessageDelete={handleMsgDelete}
  onMessageStar={handleMsgStar}
  onMessagePin={handleMsgPin}
  onMessageUnpin={handleMsgUnpin}
  chatMessages={currentChatMessages}
  chatKey={currentChatKey}
  pinnedMsgIds={currentPins.map((p) => p.msgId)}
  resolveSenderChatId={resolveSenderChatIdProp}
  findUsernameByChatId={findUsernameByChatId}
  renderSystemText={renderSystemText}
  renderTextWithLinks={renderTextWithLinks}
  onOpenMedia={setActiveMedia}
  chatBodyRef={chatBodyRef}
  scheduled={scheduled}
  formatScheduledTime={formatScheduledTime}
  onCancelScheduled={cancelScheduled}
  onRevealMedia={handleRevealMedia}
  socket={socket}
  onOpenGroup={onOpenGroup}
  onOpenDirectMessage={onOpenDirectMessage}
  setMessages={setMessages}
  setGroups={setGroups}
  uuid={uuid}
/>
  </div>
</div>
                  {/* ── Bottom input stack ── */}
          <div
            ref={replyRef}
            className="relative bg-neutral-50 dark:bg-neutral-900"
            style={{
              backgroundImage: "url(/commdoodle.jpg)",
              backgroundRepeat: "repeat",
              backgroundSize: "320px 320px",
              backgroundPosition: "center",
            }}
          >
                      {/* ── Scroll-to-bottom button ── */}
            {showScrollBtn && (
              <div
                className={
                  mobileView
                    ? "fixed right-3 z-[46] pointer-events-none transition-all duration-150"
                    : "absolute right-3 z-40 pointer-events-none transition-all duration-150"
                }
                style={
                                 mobileView
                    ? {
                        // ChatInput is a normal flex child now (no longer
                        // position:fixed — see ChatInput.js), and its own
                        // padding already folds in bottom-tab-bar/safe-area
                        // clearance, so --chat-input-h alone is the full
                        // offset; just add room for the reply/edit preview.
                      bottom: `calc(var(--chat-input-h, 64px) + ${
      (selectedReply || activeEditTarget)
        ? REPLY_PREVIEW_HEIGHT + 12
        : 12
    }px)`,
                      }
                    : {
                        bottom: (selectedReply || activeEditTarget)
                          ? `calc(100% + ${REPLY_PREVIEW_HEIGHT + 12}px)`
                          : `calc(100% + 12px)`,
                      }
                }
              >
                <button
                  onClick={handleScrollToBottom}
                  className="pointer-events-auto flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-[#202C33] shadow-[0_2px_12px_rgba(0,0,0,0.18)] border border-black/10 dark:border-white/10 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700 active:scale-95 transition-all"
                  aria-label="Scroll to bottom"
                >
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>
            )}

         {/* Pending media preview */}
<div
>
  <MediaPreviewBar
    pendingMedia={pendingMedia}
    onDismiss={clearPendingMedia}
  />
</div>
            {/* Emoji picker — desktop */}
            {showEmoji && !mobileView && !isRemovedFromGroup && (
              <div className="border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
                <EmojiPicker recentEmojis={recentEmojis} onSelect={handleEmojiSelect} />
              </div>
            )}

            
            <ChatInput
              inputRef={inputRef}
              input={input}
              pendingMedia={pendingMedia}
                mobileView={mobileView}
              setPendingMedia={setPendingMedia}
              selectedReply={selectedReply}
              setSelectedReply={setSelectedReply}
              isPaused={isPaused}
              pauseRecording={pauseRecording}
              resumeRecording={resumeRecording}
              previewUrl={voicePreviewUrl}
              isRecording={isRecording}
              startRecording={startRecording}
              stopRecording={stopRecording}
              cancelRecording={cancelRecording}
              recordingSeconds={recordingSeconds}
              showEmoji={showEmoji}
              setShowEmoji={setShowEmoji}
              fileInputRef={fileInputRef}
              handleInput={handleInput}
              sendMessage={sendMessage}
              isSending={isSending}
              disabled={isRemovedFromGroup}
              disabledMessage="You cannot send messages to this group because you were removed"
              editTarget={activeEditTarget}
              onCancelEdit={activeOnCancelEdit}
              onConfirmEdit={activeOnConfirmEdit}
              // ── Schedule ──────────────────────────────────────────
              onScheduleSend={handleOpenScheduleModal}
              // ── @mention: only pass members when in a group chat ──
              groupMembers={
                selectedItem?.type === "group"
                  ? (groupMembers[selectedItem.groupId] || liveGroup?.members || [])
                  : []
              }
              setInput={setInput}
              myChatId={myChatId}
            />


            {/* Emoji picker — mobile */}
            {showEmoji && mobileView && !isRemovedFromGroup && (
              <div className="border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
                <EmojiPicker recentEmojis={recentEmojis} onSelect={handleEmojiSelect} />
              </div>
            )}

          </div>{/* end bottom input stack */}

         <UserInfoOverlay
  open={showUserInfo}
  onClose={() => setShowUserInfo(false)}
  user={selectedItem}
  initialTab={userInfoInitialTab}
  mutualGroups={mutualGroups}
                      onOpenGroup={onOpenGroup}
            uploadImage={uploadToR2}
            socket={socket}
            myChatId={myChatId}
            isFollowing={userFollowState.isFollowing}
            followersCount={userFollowState.followersCount}
            followingCount={userFollowState.followingCount}
            onFollow={handleFollow}
            onUnfollow={handleUnfollow}
          />


         <GroupInfoOverlay
 open={showGroupMembers}
 onClose={() => setShowGroupMembers(false)}
 group={liveGroup ?? selectedItem}
 members={
   groupMembers[selectedItem?.groupId] ||
   liveGroup?.members ||
   []
 }
 inviteLink={groupInviteLink}
 socket={socket}
 myChatId={myChatId}
 setSelectedItem={setSelectedItem}
 uploadImage={uploadToR2}
 users={users}
 
 setMessages={setMessages}
 setGroups={setGroups}
 getGroupKey={getGroupKey}
 
 uuid={uuid}
/>

        </>
      ) : (
        <div className="flex items-center justify-center h-full text-neutral-500 text-sm sm:text-base">
          Select a chat
        </div>
      )}

      {/* Forward modal */}
      {forwardMessages && forwardMessages.length > 0 && (
        <ForwardModal
          messages={forwardMessages}
          users={users}
          groups={groups}
          myChatId={myChatId}
          mobileView={mobileView}
          socket={socket}
          cryptoFns={cryptoFns}
          getUserKey={getUserKey}
          getGroupKey={getGroupKey}
          onOptimisticInsert={(chatKey, optimisticMsg) => {
            setMessages((prev) => ({
              ...prev,
              [chatKey]: [...(prev[chatKey] || []), optimisticMsg],
            }));
          }}
          onClose={() => setForwardMessages(null)}
          onDone={() => {
            setForwardMessages(null);
            clearSelection();
            showToast("Forwarded");
          }}
        />
      )}

      {/* Media lightbox */}
      <MediaLightbox media={activeMedia} onClose={() => setActiveMedia(null)} />

      {/* 📌 Pin duration picker modal */}
      {showPinModal && (
        <PinDurationModal
          onConfirm={confirmPin}
          onClose={() => { setShowPinModal(false); setPinTarget(null); }}
        />
      )}

      {/* ⏰ Schedule modal */}
      {showScheduleModal && (
        <ScheduleModal
          users={users}
          groups={groups}
          preSelectedItem={selectedItem}
          onConfirm={handleConfirmSchedule}
          onClose={() => setShowScheduleModal(false)}
        />
      )}

      {/* Single-message delete modal */}
      {showDeleteModal && deleteTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-6"
          onClick={() => !isDeleting && setShowDeleteModal(false)}
        >
          <div
            className="w-full max-w-xs rounded-2xl bg-white dark:bg-neutral-900 overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3">
              <p className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                Delete this message?
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                This action cannot be undone.
              </p>
            </div>
            <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
            <div className="py-1">
              {deleteCanForEveryone && (
                <button
                  onClick={() => doSingleDelete({ forEveryone: true })}
                  disabled={isDeleting}
                  className="w-full px-5 py-3.5 text-left text-sm font-medium text-red-600 dark:text-red-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 disabled:opacity-40 transition-colors touch-manipulation"
                >
                  Delete for everyone
                </button>
              )}
              <button
                onClick={() => doSingleDelete({ forEveryone: false })}
                disabled={isDeleting}
                className="w-full px-5 py-3.5 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 disabled:opacity-40 transition-colors touch-manipulation"
              >
                Delete for me
              </button>
              <div className="h-px bg-neutral-100 dark:bg-neutral-800 mx-4" />
              <button
                onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                disabled={isDeleting}
                className="w-full px-5 py-3.5 text-left text-sm text-neutral-500 hover:bg-neutral-50 dark:hover:bg-neutral-800 active:bg-neutral-100 transition-colors touch-manipulation"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}